import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { motion } from "framer-motion";
import { MapPin, Truck } from "lucide-react";
import api from "../lib/api";
import { useRikshawStream } from "../hooks/useRikshawStream";
import { LiveStatusDot } from "../components/LiveStatusDot";

// Fix default marker icons (Leaflet bundling issue with bundlers)
const customIcon = new L.Icon({
  iconUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const STATUS_COLORS = {
  active: "#16a34a",
  on_delivery: "#E91E63",
  idle: "#D4AF37",
};

export default function MapPage() {
  const [rikshaws, setRikshaws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api
      .get("/rikshaws")
      .then((r) => setRikshaws(r.data.rikshaws || []))
      .catch((e) =>
        setErr(e?.response?.data?.detail || "Failed to load rikshaws.")
      )
      .finally(() => setLoading(false));
  }, []);

  useRikshawStream((evt) => {
    setRikshaws((list) =>
      list.map((r) =>
        r.id === evt.rikshaw_id
          ? { ...r, lat: evt.lat, lng: evt.lng, status: evt.status, last_ping_at: evt.last_ping_at }
          : r
      )
    );
  });

  return (
    <div data-testid="map-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
      >
        <div>
          <span className="label-overline text-jow-gold">Phase 0 · Live fleet</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 text-jow-navy tracking-tight">
            Six rikshaws, six cities.
          </h1>
          <p className="mt-3 text-jow-navy/60 max-w-xl">
            Each marker represents one of our beautifully decorated e-rikshaws
            currently on the road. Tap a pin to meet the driver.
          </p>
        </div>
        <div className="flex items-center gap-5 text-xs label-overline">
          {Object.entries(STATUS_COLORS).map(([k, c]) => (
            <span key={k} className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full inline-block"
                style={{ background: c }}
              />
              {k.replace("_", " ")}
            </span>
          ))}
        </div>
      </motion.div>

      <div
        className="relative border border-jow-gold/40 bg-white"
        data-testid="map-container"
        style={{ height: "560px" }}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-jow-cream/70 z-[1000]">
            <p className="label-overline text-jow-navy/60">Loading rikshaws…</p>
          </div>
        )}
        {err && (
          <div className="absolute inset-0 flex items-center justify-center bg-jow-cream/70 z-[1000]">
            <p className="text-sm text-jow-pink" data-testid="map-error">
              {err}
            </p>
          </div>
        )}
        <MapContainer
          center={[22.9734, 78.6569]}
          zoom={5}
          minZoom={4}
          scrollWheelZoom={true}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {rikshaws.map((r) => (
            <Marker
              key={r.id}
              position={[r.lat, r.lng]}
              icon={customIcon}
            >
              <Popup>
                <div data-testid={`popup-${r.code}`}>
                  <p className="label-overline" style={{ color: "#D4AF37" }}>
                    {r.city}
                  </p>
                  <p
                    className="font-display"
                    style={{
                      fontSize: "1.1rem",
                      color: "#0A1F44",
                      marginTop: 4,
                      fontFamily: '"Playfair Display", serif',
                    }}
                  >
                    {r.code}
                  </p>
                  <p style={{ fontSize: "0.85rem", marginTop: 8, color: "#0A1F44" }}>
                    Driver: <strong>{r.driver_name}</strong>
                  </p>
                  <p
                    style={{
                      fontSize: "0.75rem",
                      marginTop: 6,
                      color: STATUS_COLORS[r.status] || "#0A1F44",
                      textTransform: "uppercase",
                      letterSpacing: "0.18em",
                    }}
                  >
                    {r.status.replace("_", " ")}
                  </p>
                  <div style={{ marginTop: 8 }}>
                    <LiveStatusDot lastPingAt={r.last_ping_at} testid={`popup-dot-${r.code}`} />
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Rikshaw list below the map */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="rikshaw-list">
        {rikshaws.map((r) => (
          <div
            key={`card-${r.id}`}
            className="border border-jow-navy/10 bg-white p-5 hover:border-jow-gold/60 transition-colors"
            data-testid={`rikshaw-card-${r.code}`}
          >
            <div className="flex items-center gap-3">
              <span
                className="inline-flex w-9 h-9 items-center justify-center"
                style={{
                  background: `${STATUS_COLORS[r.status] || "#0A1F44"}15`,
                  color: STATUS_COLORS[r.status] || "#0A1F44",
                }}
              >
                <Truck size={16} />
              </span>
              <div>
                <p className="font-display text-lg text-jow-navy leading-tight">
                  {r.code}
                </p>
                <p className="text-xs text-jow-navy/60 flex items-center gap-1">
                  <MapPin size={12} /> {r.city}
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm text-jow-navy/70">{r.driver_name}</p>
            <div className="mt-2 flex items-center justify-between">
              <p
                className="text-[10px] tracking-[0.25em] uppercase"
                style={{ color: STATUS_COLORS[r.status] || "#0A1F44" }}
              >
                {r.status.replace("_", " ")}
              </p>
              <LiveStatusDot lastPingAt={r.last_ping_at} testid={`card-dot-${r.code}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
